import { compactFlatten, each, objectHasKeys, toMilliseconds } from '@art-suite/art-core-ts';
import { createInternalServerError, createNotFoundError } from '@kaiizen/zod-less-api';
import { ScanOrScanTemplatePartTypes } from '../CommonScanSchemas';
import { SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH, SCAN_PART_TYPES_BY_DEPTH, ScanPart, ScanRoot, validateScanDataStructureRecursionDepth } from '../ScanRoot';
import { getScanDataInMemoryByTypeAndId, required, ScanDataInMemoryByTypeAndId } from './ScanDataInMemoryLib';
import { ScanDataInMemory } from './ScanDataInMemoryModels';

export const getScanPartFromScanDataInMemoryWithoutChildren = (scanDataInMemory: ScanDataInMemory): ScanPart => {
  const result = {
    // all fields encoded in the data field
    ...scanDataInMemory.data as ScanPart,

    // fields that are not encoded in the data field
    id: scanDataInMemory.scanPartId,
    updatedAt: toMilliseconds(scanDataInMemory.updatedAt),
    templateId: required(scanDataInMemory.templateScanPartId, 'templateId'),
  };
  return result;
};

// ***********************************************************************************
// ScanDataInMemory
// ***********************************************************************************
const EMPTY_OBJECT = {};
const getUnparsedScanRecursively = (scanDataInMemory: ScanDataInMemory, allScanDataInMemory: ScanDataInMemoryByTypeAndId, recursionDepth: number): ScanPart => {
  validateScanDataStructureRecursionDepth(recursionDepth);
  const childrenFieldName = SCAN_CHILDREN_FIELD_NAMES_BY_DEPTH[recursionDepth];
  const childPartType = SCAN_PART_TYPES_BY_DEPTH[recursionDepth + 1];
  const allChildrenMatchingChildPartType = allScanDataInMemory[childPartType] ?? EMPTY_OBJECT;
  const result = getScanPartFromScanDataInMemoryWithoutChildren(scanDataInMemory);

  if (childrenFieldName && objectHasKeys(scanDataInMemory.childrenScanPartIds)) {
    // children
    (result as any)[childrenFieldName] = scanDataInMemory.childrenScanPartIds
      ? compactFlatten(
        scanDataInMemory.childrenScanPartIds?.map(childId => {
          const child = allChildrenMatchingChildPartType[childId];
          if (!child) {
            const matchingChildFoundWithWrongType = ScanOrScanTemplatePartTypes.find(type => allScanDataInMemory[type]?.[childId]);
            console.error(`getUnparsedScanRecursively: Parent ${scanDataInMemory.type}'s child not found.`, {
              parentScanPartId: scanDataInMemory.scanPartId,
              childScanPartId: childId,
              childPartType,
              scanId: scanDataInMemory.scanId,
              parent: scanDataInMemory,
              matchingChildFoundWithWrongType,
            });
            return null;
          }
          return getUnparsedScanRecursively(child, allScanDataInMemory, recursionDepth + 1);
        }),
      )
      : null;
  }
  return result;
};

/**
 * Pure function: Get parsed ScanRoot from ScanDataInMemory records (no database operations)
 * @param allScanPartsArray
 * @returns
 */
export const getScanRootFromScanData = (allScanPartsArray: ScanDataInMemory[]): ScanRoot => {
  const allScanDataInMemory = getScanDataInMemoryByTypeAndId(allScanPartsArray);
  const [scanRoot, ...restScanData] = allScanDataInMemory.scan ? Object.values(allScanDataInMemory.scan) : [];
  if (!scanRoot) throw createNotFoundError('Could not find ScanRoot');
  if (restScanData.length > 0) {
    throw createInternalServerError(`Found ${restScanData.length + 1} ScanRoots, expected 1.`);
  }
  return getUnparsedScanRecursively(scanRoot, allScanDataInMemory, 0) as ScanRoot;
};

/**
 * return the latest instance of scan ScanData by ScanPartId
 * @param scanDataInMemory
 * @param scanDataInMemoryToMerge
 * @returns
 */
export const mergeScanDataInMemory = (scanDataList1: ScanDataInMemory[], scanDataList2: ScanDataInMemory[]): ScanDataInMemory[] => {
  if (scanDataList1.length === 0) return scanDataList2;
  if (scanDataList2.length === 0) return scanDataList1;
  const resultMap = new Map<string, ScanDataInMemory>();
  const addEntry = (scanData: ScanDataInMemory) => {
    const existing = resultMap.get(scanData.scanPartId);
    if (!existing || existing.updatedAt <= scanData.updatedAt) {
      resultMap.set(scanData.scanPartId, scanData);
    }
  };
  each(scanDataList1, addEntry);
  each(scanDataList2, addEntry);
  return [...resultMap.values()];
};
